// Bot Settings Drawer for Bot Manager v2 (PRD §1.3, WP-07).
// 7 tabs: General, Webhook Triggers, Business Hours, Shared Inbox, Google, Email & SMS, Autoresponder.
import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import { useToast } from '../ui/Toast';
import Drawer from '../ui/Drawer';
import Toggle from '../ui/Toggle';
import Button from '../ui/Button';
import { useLabels } from '../../lib/crm';
import {
  botSettingsGet,
  botSettingsSave,
  defaultBotSettings,
  TIMEZONES,
  TIME_STEPS_30MIN,
  DAYS_OF_WEEK,
  type BotSettings,
  type DayOfWeek,
} from '../../lib/bots';
import {
  integrationProfileList,
  type IntegrationProfileDoc,
} from '../../lib/integrations';

interface BotSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  integrationId: string;
  botLabel?: string;
  onSaved?: () => void;
}

type SettingsTab =
  | 'general'
  | 'webhook'
  | 'businessHours'
  | 'sharedInbox'
  | 'google'
  | 'emailSms'
  | 'autoresponder';

export default function BotSettingsDrawer({
  open,
  onClose,
  integrationId,
  botLabel,
  onSaved,
}: BotSettingsDrawerProps) {
  const { t } = useI18n();
  const toast = useToast();
  const { labels } = useLabels();

  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [settings, setSettings] = useState<BotSettings>(defaultBotSettings());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [emailProfiles, setEmailProfiles] = useState<IntegrationProfileDoc[]>([]);
  const [smsProfiles, setSmsProfiles] = useState<IntegrationProfileDoc[]>([]);
  const [autoresponderProfiles, setAutoresponderProfiles] = useState<IntegrationProfileDoc[]>([]);

  useEffect(() => {
    if (!open) return;
    integrationProfileList({ kind: 'email' })
      .then((r) => r.data.ok && setEmailProfiles(r.data.profiles))
      .catch((err) => toast.error(err.message || t('bots.loadEmailProfilesFailed') || 'Failed to load email profiles'));
    integrationProfileList({ kind: 'sms' })
      .then((r) => r.data.ok && setSmsProfiles(r.data.profiles))
      .catch((err) => toast.error(err.message || t('bots.loadSmsProfilesFailed') || 'Failed to load SMS profiles'));
    integrationProfileList({ kind: 'autoresponder' })
      .then((r) => r.data.ok && setAutoresponderProfiles(r.data.profiles))
      .catch((err) => toast.error(err.message || t('bots.loadAutoresponderProfilesFailed') || 'Failed to load autoresponder profiles'));
  }, [open]);

  useEffect(() => {
    if (!open || !integrationId) return;
    setLoading(true);
    botSettingsGet({ integrationId })
      .then((res) => {
        if (res.data.ok && res.data.settings) {
          setSettings(res.data.settings);
        }
      })
      .catch((err) => {
        console.error('[BotSettingsDrawer] load error', err);
        toast.error((err as Error).message || 'Failed to load bot settings');
      })
      .finally(() => setLoading(false));
  }, [open, integrationId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await botSettingsSave({
        integrationId,
        settings,
      });
      if (res.data.ok && res.data.settings) {
        setSettings(res.data.settings);
      }
      toast.success(t('bots.settingsSaved') || 'Bot settings saved successfully');
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('[BotSettingsDrawer] save error', err);
      toast.error((err as Error).message || 'Failed to save bot settings');
    } finally {
      setSaving(false);
    }
  };

  const updateDayHours = (day: DayOfWeek, off: boolean, openTime?: string, closeTime?: string) => {
    setSettings((prev) => {
      const current = prev.businessHours.days[day];
      const nextDays = { ...prev.businessHours.days };
      if (off) {
        nextDays[day] = 'off';
      } else {
        const prevOpen = typeof current === 'object' ? current.open : '09:00';
        const prevClose = typeof current === 'object' ? current.close : '18:00';
        nextDays[day] = {
          open: openTime ?? prevOpen,
          close: closeTime ?? prevClose,
        };
      }
      return {
        ...prev,
        businessHours: {
          ...prev.businessHours,
          days: nextDays,
        },
      };
    });
  };

  const tabs: { id: SettingsTab; label: string }[] = [
    { id: 'general', label: t('bots.tabs.general') || 'General' },
    { id: 'webhook', label: t('bots.tabs.webhook') || 'Webhook' },
    { id: 'businessHours', label: t('bots.tabs.businessHours') || 'Business Hours' },
    { id: 'sharedInbox', label: t('bots.tabs.sharedInbox') || 'Shared Inbox' },
    { id: 'google', label: t('bots.tabs.google') || 'Google' },
    { id: 'emailSms', label: t('bots.tabs.emailSms') || 'Email & SMS' },
    { id: 'autoresponder', label: t('bots.tabs.autoresponder') || 'Auto Responder' },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`${t('bots.settingsTitle') || 'Bot Settings'}${botLabel ? ` · ${botLabel}` : ''}`}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2.5">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button size="sm" onClick={handleSave} loading={saving}>
            {t('common.save') || 'Save Settings'}
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="grid h-64 place-items-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Tab navigation */}
          <div className="flex flex-wrap gap-1.5 border-b border-line pb-2.5 dark:border-slate-800">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  activeTab === tab.id
                    ? 'bg-brand-50 text-brand dark:bg-brand-950/50 dark:text-brand-tint'
                    : 'text-ink-2 hover:bg-surface dark:text-slate-400 dark:hover:bg-slate-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 1. GENERAL TAB */}
          {activeTab === 'general' && (
            <div className="space-y-5 text-xs">
              <div>
                <label className="block font-semibold text-ink dark:text-slate-200">
                  {t('bots.general.handoffEmail') || 'Human Agent Handoff Email'}
                </label>
                <p className="mt-0.5 text-ink-3 dark:text-slate-400">
                  {t('bots.general.handoffEmailDesc') ||
                    'Receive an email alert whenever a customer requests human agent intervention.'}
                </p>
                <input
                  type="email"
                  value={settings.humanHandoffEmail || ''}
                  onChange={(e) =>
                    setSettings((p) => ({ ...p, humanHandoffEmail: e.target.value || null }))
                  }
                  placeholder="agent@company.com"
                  className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>

              <div className="flex items-center justify-between rounded-xl border border-line bg-surface/30 p-3.5 dark:border-slate-800 dark:bg-slate-800/30">
                <div>
                  <span className="font-semibold text-ink dark:text-slate-200">
                    {t('bots.general.noMatchReply') || 'No-Match Reply'}
                  </span>
                  <p className="mt-0.5 text-ink-3 dark:text-slate-400">
                    {t('bots.general.noMatchReplyDesc') ||
                      'Send an automatic fallback response when user input does not match any keyword or active flow.'}
                  </p>
                </div>
                <Toggle
                  on={settings.noMatchReplyEnabled}
                  onChange={(v) => setSettings((p) => ({ ...p, noMatchReplyEnabled: v }))}
                  label={t('bots.general.noMatchReply') || 'No-Match Reply'}
                />
              </div>

              <div className="flex items-center justify-between rounded-xl border border-line bg-surface/30 p-3.5 dark:border-slate-800 dark:bg-slate-800/30">
                <div>
                  <span className="font-semibold text-ink dark:text-slate-200">
                    {t('bots.general.agentOnMobile') || 'Bot Replies While Agent on Mobile'}
                  </span>
                  <p className="mt-0.5 text-ink-3 dark:text-slate-400">
                    {t('bots.general.agentOnMobileDesc') ||
                      'Allow automated bot replies to continue even when agents are logged into the WhatsApp mobile app.'}
                  </p>
                </div>
                <Toggle
                  on={settings.botRepliesWhileAgentOnMobile}
                  onChange={(v) => setSettings((p) => ({ ...p, botRepliesWhileAgentOnMobile: v }))}
                  label={t('bots.general.agentOnMobile') || 'Bot Replies While Agent on Mobile'}
                />
              </div>

              <div className="rounded-xl border border-line bg-surface/30 p-3.5 dark:border-slate-800 dark:bg-slate-800/30 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-ink dark:text-slate-200">
                      {t('bots.general.getStarted') || 'Get Started Message'}
                    </span>
                    <p className="mt-0.5 text-ink-3 dark:text-slate-400">
                      {t('bots.general.getStartedDesc') ||
                        'Send an introductory welcome message or flow to first-time contacts.'}
                    </p>
                  </div>
                  <Toggle
                    on={settings.getStartedEnabled}
                    onChange={(v) => setSettings((p) => ({ ...p, getStartedEnabled: v }))}
                    label={t('bots.general.getStarted') || 'Get Started Message'}
                  />
                </div>

                {settings.getStartedEnabled && (
                  <div className="border-t border-line pt-2.5 dark:border-slate-700">
                    <label className="block font-medium text-ink-2 dark:text-slate-300">
                      {t('bots.general.getStartedFrequency') || 'Get Started Frequency'}
                    </label>
                    <select
                      value={settings.getStartedFrequency}
                      onChange={(e) =>
                        setSettings((p) => ({
                          ...p,
                          getStartedFrequency: e.target.value as 'every_time' | 'once_a_day',
                        }))
                      }
                      className="mt-1 rounded-lg border border-line bg-white px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    >
                      <option value="every_time">
                        {t('bots.general.freqEveryTime') || 'Every time'}
                      </option>
                      <option value="once_a_day">
                        {t('bots.general.freqOnceADay') || 'Once a day'}
                      </option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 2. WEBHOOK TRIGGERS TAB */}
          {activeTab === 'webhook' && (
            <div className="space-y-4 text-xs">
              <p className="text-ink-3 dark:text-slate-400">
                {t('bots.webhook.desc') ||
                  'Select events that dispatch outbound webhooks configured in Webhook Workflows.'}
              </p>

              {[
                {
                  key: 'incoming' as const,
                  title: t('bots.webhook.incoming') || 'Incoming Message',
                  desc: t('bots.webhook.incomingDesc') || 'Trigger webhook whenever an inbound customer message arrives.',
                },
                {
                  key: 'outgoing' as const,
                  title: t('bots.webhook.outgoing') || 'Outgoing Message',
                  desc: t('bots.webhook.outgoingDesc') || 'Trigger webhook whenever an outbound message is sent by bot or agent.',
                },
                {
                  key: 'status' as const,
                  title: t('bots.webhook.status') || 'Message Status Change',
                  desc: t('bots.webhook.statusDesc') || 'Trigger webhook on delivery updates (delivered, read, failed).',
                },
                {
                  key: 'conversationStatus' as const,
                  title: t('bots.webhook.conversation') || 'Conversation Status Change',
                  desc: t('bots.webhook.conversationDesc') || 'Trigger webhook when conversation is resolved, archived, or assigned.',
                },
              ].map((item) => (
                <div
                  key={item.key}
                  className="flex items-center justify-between rounded-xl border border-line bg-surface/30 p-3.5 dark:border-slate-800 dark:bg-slate-800/30"
                >
                  <div>
                    <span className="font-semibold text-ink dark:text-slate-200">{item.title}</span>
                    <p className="mt-0.5 text-ink-3 dark:text-slate-400">{item.desc}</p>
                  </div>
                  <Toggle
                    on={settings.webhookTriggers[item.key]}
                    onChange={(v) =>
                      setSettings((p) => ({
                        ...p,
                        webhookTriggers: { ...p.webhookTriggers, [item.key]: v },
                      }))
                    }
                    label={item.title}
                  />
                </div>
              ))}
            </div>
          )}

          {/* 3. BUSINESS HOURS TAB */}
          {activeTab === 'businessHours' && (
            <div className="space-y-5 text-xs">
              <div>
                <label className="block font-semibold text-ink dark:text-slate-200">
                  {t('bots.hours.timezone') || 'Timezone'}
                </label>
                <select
                  value={settings.businessHours.timezone}
                  onChange={(e) =>
                    setSettings((p) => ({
                      ...p,
                      businessHours: { ...p.businessHours, timezone: e.target.value },
                    }))
                  }
                  className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>

              <div className="divide-y divide-line rounded-xl border border-line bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
                {DAYS_OF_WEEK.map((day) => {
                  const dayHour = settings.businessHours.days[day];
                  const isOff = dayHour === 'off';
                  const openVal = typeof dayHour === 'object' ? dayHour.open : '09:00';
                  const closeVal = typeof dayHour === 'object' ? dayHour.close : '18:00';

                  return (
                    <div
                      key={day}
                      className="flex flex-wrap items-center justify-between gap-3 p-3 sm:px-4"
                    >
                      <div className="flex items-center gap-3 w-32">
                        <span className="font-bold uppercase text-ink dark:text-slate-200">
                          {t(`bots.day.${day}`) || day}
                        </span>
                        {isOff && (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                            {t('bots.hours.dayOff') || 'Day Off'}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        {!isOff ? (
                          <div className="flex items-center gap-1.5">
                            <select
                              value={openVal}
                              onChange={(e) => updateDayHours(day, false, e.target.value, closeVal)}
                              className="rounded-lg border border-line bg-surface/50 px-2 py-1 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              {TIME_STEPS_30MIN.map((step) => (
                                <option key={step} value={step}>
                                  {step}
                                </option>
                              ))}
                            </select>
                            <span className="text-ink-3 dark:text-slate-500">—</span>
                            <select
                              value={closeVal}
                              onChange={(e) => updateDayHours(day, false, openVal, e.target.value)}
                              className="rounded-lg border border-line bg-surface/50 px-2 py-1 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            >
                              {TIME_STEPS_30MIN.map((step) => (
                                <option key={step} value={step}>
                                  {step}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : null}

                        <label className="inline-flex items-center gap-1.5 cursor-pointer text-ink-3 dark:text-slate-400">
                          <input
                            type="checkbox"
                            checked={!isOff}
                            onChange={(e) => updateDayHours(day, !e.target.checked)}
                            className="rounded border-line text-brand focus:ring-brand"
                          />
                          <span>{t('bots.hours.open') || 'Open'}</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 4. SHARED INBOX TAB */}
          {activeTab === 'sharedInbox' && (
            <div className="space-y-4 text-xs">
              {/* Agent Signature */}
              <div className="rounded-xl border border-line bg-surface/30 p-3.5 dark:border-slate-800 dark:bg-slate-800/30 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-ink dark:text-slate-200">
                      {t('bots.inbox.signature') || 'Agent Signature'}
                    </span>
                    <p className="mt-0.5 text-ink-3 dark:text-slate-400">
                      {t('bots.inbox.signatureDesc') ||
                        'Automatically append agent signature to outgoing manual messages.'}
                    </p>
                  </div>
                  <Toggle
                    on={settings.sharedInbox.signatureEnabled}
                    onChange={(v) =>
                      setSettings((p) => ({
                        ...p,
                        sharedInbox: { ...p.sharedInbox, signatureEnabled: v },
                      }))
                    }
                    label={t('bots.inbox.signature') || 'Agent Signature'}
                  />
                </div>
                {settings.sharedInbox.signatureEnabled && (
                  <input
                    type="text"
                    value={settings.sharedInbox.signature}
                    onChange={(e) =>
                      setSettings((p) => ({
                        ...p,
                        sharedInbox: { ...p.sharedInbox, signature: e.target.value },
                      }))
                    }
                    placeholder="— Support Team"
                    className="w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                )}
              </div>

              {/* Human Takeover toggles */}
              <div className="flex items-center justify-between rounded-xl border border-line bg-surface/30 p-3.5 dark:border-slate-800 dark:bg-slate-800/30">
                <div>
                  <span className="font-semibold text-ink dark:text-slate-200">
                    {t('bots.inbox.disableBotTakeover') || 'Disable Bot on Human Takeover'}
                  </span>
                  <p className="mt-0.5 text-ink-3 dark:text-slate-400">
                    {t('bots.inbox.disableBotTakeoverDesc') ||
                      'Pause bot reply when an agent manually replies in conversation.'}
                  </p>
                </div>
                <Toggle
                  on={settings.sharedInbox.disableBotOnTakeover}
                  onChange={(v) =>
                    setSettings((p) => ({
                      ...p,
                      sharedInbox: { ...p.sharedInbox, disableBotOnTakeover: v },
                    }))
                  }
                  label={t('bots.inbox.disableBotTakeover') || 'Disable Bot on Human Takeover'}
                />
              </div>

              <div className="flex items-center justify-between rounded-xl border border-line bg-surface/30 p-3.5 dark:border-slate-800 dark:bg-slate-800/30">
                <div>
                  <span className="font-semibold text-ink dark:text-slate-200">
                    {t('bots.inbox.disableAiTakeover') || 'Disable AI on Human Takeover'}
                  </span>
                  <p className="mt-0.5 text-ink-3 dark:text-slate-400">
                    {t('bots.inbox.disableAiTakeoverDesc') ||
                      'Pause AI agent reply when a human agent replies in conversation.'}
                  </p>
                </div>
                <Toggle
                  on={settings.sharedInbox.disableAiOnTakeover}
                  onChange={(v) =>
                    setSettings((p) => ({
                      ...p,
                      sharedInbox: { ...p.sharedInbox, disableAiOnTakeover: v },
                    }))
                  }
                  label={t('bots.inbox.disableAiTakeover') || 'Disable AI on Human Takeover'}
                />
              </div>

              {/* Auto Re-enable Timer */}
              <div>
                <label className="block font-semibold text-ink dark:text-slate-200">
                  {t('bots.inbox.autoEnable') || 'Auto Re-enable Bot / AI (Minutes)'}
                </label>
                <p className="mt-0.5 text-ink-3 dark:text-slate-400">
                  {t('bots.inbox.autoEnableDesc') ||
                    'Automatically resume bot and AI automation after N minutes of agent inactivity.'}
                </p>
                <input
                  type="number"
                  min="0"
                  max="10080"
                  value={settings.sharedInbox.autoEnableAfterMin}
                  onChange={(e) =>
                    setSettings((p) => ({
                      ...p,
                      sharedInbox: {
                        ...p.sharedInbox,
                        autoEnableAfterMin: parseInt(e.target.value, 10) || 0,
                      },
                    }))
                  }
                  className="mt-1.5 w-36 rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>

              {/* Typing Indicator */}
              <div className="flex items-center justify-between rounded-xl border border-line bg-surface/30 p-3.5 dark:border-slate-800 dark:bg-slate-800/30">
                <div>
                  <span className="font-semibold text-ink dark:text-slate-200">
                    {t('bots.inbox.typing') || 'Typing-On Indicator'}
                  </span>
                  <p className="mt-0.5 text-ink-3 dark:text-slate-400">
                    {t('bots.inbox.typingDesc') ||
                      'Show WhatsApp typing indicator when bot processes reply.'}
                  </p>
                </div>
                <Toggle
                  on={settings.sharedInbox.typingIndicator}
                  onChange={(v) =>
                    setSettings((p) => ({
                      ...p,
                      sharedInbox: { ...p.sharedInbox, typingIndicator: v },
                    }))
                  }
                  label={t('bots.inbox.typing') || 'Typing-On Indicator'}
                />
              </div>

              {/* Session Expiries */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block font-semibold text-ink dark:text-slate-200">
                    {t('bots.inbox.flowExpiry') || 'Input Flow Session Expiry (Minutes)'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10080"
                    value={settings.sharedInbox.inputFlowExpiryMin}
                    onChange={(e) =>
                      setSettings((p) => ({
                        ...p,
                        sharedInbox: {
                          ...p.sharedInbox,
                          inputFlowExpiryMin: parseInt(e.target.value, 10) || 30,
                        },
                      }))
                    }
                    className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-ink dark:text-slate-200">
                    {t('bots.inbox.humanExpiry') || 'Chat with Human Expiry (Minutes)'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10080"
                    value={settings.sharedInbox.humanChatExpiryMin}
                    onChange={(e) =>
                      setSettings((p) => ({
                        ...p,
                        sharedInbox: {
                          ...p.sharedInbox,
                          humanChatExpiryMin: parseInt(e.target.value, 10) || 120,
                        },
                      }))
                    }
                    className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                </div>
              </div>

              {/* Failed Delivery Label */}
              <div>
                <label className="block font-semibold text-ink dark:text-slate-200">
                  {t('bots.inbox.failedLabel') || 'Label for Failed Message Delivery'}
                </label>
                <p className="mt-0.5 text-ink-3 dark:text-slate-400">
                  {t('bots.inbox.failedLabelDesc') ||
                    'Automatically attach this label to subscriber if Meta reports delivery failure.'}
                </p>
                <select
                  value={settings.sharedInbox.failedDeliveryLabelId || ''}
                  onChange={(e) =>
                    setSettings((p) => ({
                      ...p,
                      sharedInbox: {
                        ...p.sharedInbox,
                        failedDeliveryLabelId: e.target.value || null,
                      },
                    }))
                  }
                  className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="">{t('common.none') || 'None'}</option>
                  {labels.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* 5. GOOGLE TAB */}
          {activeTab === 'google' && (
            <div className="space-y-4 text-xs">
              <p className="text-ink-3 dark:text-slate-400">
                {t('bots.google.desc') ||
                  'Configure Google Spreadsheets and Google Contacts sync for subscribers on this number.'}
              </p>

              <div>
                <label className="block font-semibold text-ink dark:text-slate-200">
                  {t('bots.google.sheetId') || 'Default Google Spreadsheet ID'}
                </label>
                <input
                  type="text"
                  value={settings.google.sheetId || ''}
                  onChange={(e) =>
                    setSettings((p) => ({
                      ...p,
                      google: { ...p.google, sheetId: e.target.value || null },
                    }))
                  }
                  placeholder="e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                  className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>

              <div>
                <label className="block font-semibold text-ink dark:text-slate-200">
                  {t('bots.google.contactsAccount') || 'Google Contacts Account ID'}
                </label>
                <input
                  type="text"
                  value={settings.google.contactsAccountId || ''}
                  onChange={(e) =>
                    setSettings((p) => ({
                      ...p,
                      google: { ...p.google, contactsAccountId: e.target.value || null },
                    }))
                  }
                  placeholder="e.g. user@gmail.com"
                  className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>
            </div>
          )}

          {/* 6. EMAIL & SMS TAB */}
          {activeTab === 'emailSms' && (
            <div className="space-y-6 text-xs">
              {/* Email Profile */}
              <div className="rounded-xl border border-line bg-surface/30 p-4 dark:border-slate-800 dark:bg-slate-800/30 space-y-3">
                <h4 className="font-bold text-ink dark:text-slate-200">
                  {t('bots.email.title') || 'Email Automated Responder'}
                </h4>
                <div>
                  <label className="block font-medium text-ink-2 dark:text-slate-300">
                    {t('bots.email.savedProfile') || 'Email Delivery Profile (WP-11)'}
                  </label>
                  <select
                    value={settings.email.profileId || ''}
                    onChange={(e) =>
                      setSettings((p) => ({
                        ...p,
                        email: { ...p.email, profileId: e.target.value || null },
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    <option value="">{t('common.none') || '-- None (Disabled) --'}</option>
                    {emailProfiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.provider.toUpperCase()})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-medium text-ink-2 dark:text-slate-300">
                    {t('bots.email.subject') || 'Email Subject'}
                  </label>
                  <input
                    type="text"
                    value={settings.email.subject || ''}
                    onChange={(e) =>
                      setSettings((p) => ({
                        ...p,
                        email: { ...p.email, subject: e.target.value || null },
                      }))
                    }
                    placeholder="Thank you for contacting us!"
                    className="mt-1 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                </div>
                <div>
                  <label className="block font-medium text-ink-2 dark:text-slate-300">
                    {t('bots.email.body') || 'Email Body'}
                  </label>
                  <textarea
                    rows={3}
                    value={settings.email.body || ''}
                    onChange={(e) =>
                      setSettings((p) => ({
                        ...p,
                        email: { ...p.email, body: e.target.value || null },
                      }))
                    }
                    placeholder="Hello {{name}}, our representative will reach out soon..."
                    className="mt-1 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                </div>
              </div>

              {/* SMS Profile */}
              <div className="rounded-xl border border-line bg-surface/30 p-4 dark:border-slate-800 dark:bg-slate-800/30 space-y-3">
                <h4 className="font-bold text-ink dark:text-slate-200">
                  {t('bots.sms.title') || 'SMS Automated Responder'}
                </h4>
                <div>
                  <label className="block font-medium text-ink-2 dark:text-slate-300">
                    {t('bots.sms.savedProfile') || 'SMS Gateway Profile (WP-11)'}
                  </label>
                  <select
                    value={settings.sms.profileId || ''}
                    onChange={(e) =>
                      setSettings((p) => ({
                        ...p,
                        sms: { ...p.sms, profileId: e.target.value || null },
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    <option value="">{t('common.none') || '-- None (Disabled) --'}</option>
                    {smsProfiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.provider.toUpperCase()})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-medium text-ink-2 dark:text-slate-300">
                    {t('bots.sms.body') || 'SMS Message'}
                  </label>
                  <textarea
                    rows={3}
                    value={settings.sms.body || ''}
                    onChange={(e) =>
                      setSettings((p) => ({
                        ...p,
                        sms: { ...p.sms, body: e.target.value || null },
                      }))
                    }
                    placeholder="Your request has been received. Track code: {{key}}"
                    className="mt-1 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                </div>
              </div>
            </div>
          )}

          {/* 7. AUTORESPONDER TAB */}
          {activeTab === 'autoresponder' && (
            <div className="space-y-4 text-xs">
              <div className="rounded-xl border border-dashed border-line p-4 text-center dark:border-slate-800 bg-surface/20">
                <p className="text-ink-3 dark:text-slate-400">
                  {t('bots.autoresponder.note') ||
                    'Sync collected leads to Mailchimp, Brevo, ActiveCampaign, Mautic, or MailWizz using saved profiles.'}
                </p>
              </div>

              <div>
                <label className="block font-semibold text-ink dark:text-slate-200">
                  {t('bots.autoresponder.savedProfile') || 'Saved Auto Responder Profile'}
                </label>
                <select
                  value={
                    autoresponderProfiles.find(
                      (p) =>
                        p.provider === settings.autoresponder.provider &&
                        (p.config.listId === settings.autoresponder.listId || p.config.listUid === settings.autoresponder.listId)
                    )?.id || ''
                  }
                  onChange={(e) => {
                    const prof = autoresponderProfiles.find((p) => p.id === e.target.value);
                    if (prof) {
                      setSettings((p) => ({
                        ...p,
                        autoresponder: {
                          provider: prof.provider,
                          listId: prof.config.listId || prof.config.listUid || null,
                        },
                      }));
                    }
                  }}
                  className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="">{t('bots.autoresponder.selectProfile') || '-- Select Saved Profile --'}</option>
                  {autoresponderProfiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.provider.toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-ink dark:text-slate-200">
                  {t('bots.autoresponder.provider') || 'Provider'}
                </label>
                <select
                  value={settings.autoresponder.provider || ''}
                  onChange={(e) =>
                    setSettings((p) => ({
                      ...p,
                      autoresponder: {
                        ...p.autoresponder,
                        provider: e.target.value || null,
                      },
                    }))
                  }
                  className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="">{t('common.none') || 'None'}</option>
                  <option value="mailchimp">Mailchimp</option>
                  <option value="brevo">Brevo</option>
                  <option value="activecampaign">ActiveCampaign</option>
                  <option value="mautic">Mautic</option>
                  <option value="mailwizz">MailWizz</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold text-ink dark:text-slate-200">
                  {t('bots.autoresponder.listId') || 'Audience / List ID'}
                </label>
                <input
                  type="text"
                  value={settings.autoresponder.listId || ''}
                  onChange={(e) =>
                    setSettings((p) => ({
                      ...p,
                      autoresponder: { ...p.autoresponder, listId: e.target.value || null },
                    }))
                  }
                  placeholder="e.g. list_8849201"
                  className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
